/**
 * REChecklist Accounts API (MariaDB / PHP on shared hosting).
 * Base URL from VITE_ACCOUNTS_API_URL, e.g. https://rechecklist.pro/accounts-api
 */

const TOKEN_KEY = "dh_accounts_token";

export function getAccountsApiBase() {
  const raw = String(import.meta.env.VITE_ACCOUNTS_API_URL || "").trim();
  if (raw) return raw.replace(/\/+$/, "");
  if (typeof window !== "undefined") {
    const host = window.location.hostname || "";
    if (host === "rechecklist.pro" || host === "www.rechecklist.pro") {
      return "/accounts-api";
    }
  }
  return "";
}

export function isAccountsApiEnabled() {
  return Boolean(getAccountsApiBase());
}

export function getAccountsToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setAccountsToken(token) {
  if (!token) {
    clearAccountsToken();
    return;
  }
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearAccountsToken() {
  localStorage.removeItem(TOKEN_KEY);
}

async function parseJsonResponse(res) {
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  return { data, text };
}

/**
 * @returns {Promise<{ ok: true, token: string, session: object, must_rotate?: boolean, expires_at?: string }>}
 */
export async function accountsLogin({ email, password }) {
  const base = getAccountsApiBase();
  if (!base) throw new Error("Accounts API is not configured");

  let res;
  try {
    res = await fetch(`${base}/login.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ email, password }),
    });
  } catch {
    const err = new Error("Accounts service unreachable");
    err.code = "NETWORK";
    throw err;
  }

  const { data, text } = await parseJsonResponse(res);
  if (!res.ok || !data?.ok) {
    const err = new Error(data?.error || text || "Login failed");
    err.code = res.status === 401 ? "AUTH" : "HTTP";
    err.status = res.status;
    throw err;
  }
  return data;
}

/**
 * @returns {Promise<{ ok: true, user: object }>}
 */
export async function accountsRegister(payload) {
  const base = getAccountsApiBase();
  if (!base) throw new Error("Accounts API is not configured");

  let res;
  try {
    res = await fetch(`${base}/register.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(payload),
    });
  } catch {
    const err = new Error("Accounts service unreachable");
    err.code = "NETWORK";
    throw err;
  }

  const { data, text } = await parseJsonResponse(res);
  if (!res.ok || !data?.ok) {
    const err = new Error(data?.error || text || "Registration failed");
    err.code = res.status === 409 ? "EXISTS" : "HTTP";
    err.status = res.status;
    throw err;
  }
  return data;
}
